import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Search,
  Wrench,
  AlertTriangle,
  CheckCircle2,
  Filter,
  Calendar,
  Box,
  MapPin,
  FileText,
  Nfc,
} from "lucide-react";
import { MoldFormDialog } from "@/components/Molds/MoldFormDialog";
import { MoldDetails } from "@/components/Molds/MoldDetails";
import { format, isBefore, addDays } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface Mold {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  material: string | null;
  dimensions: string | null;
  location: string | null;
  current_location: string | null;
  shelf_location: string | null;
  current_work_order_id: string | null;
  nfc_tag_uid: string | null;
  status: string;
  current_uses: number;
  max_uses: number | null;
  last_maintenance_date: string | null;
  next_maintenance_date: string | null;
  is_active: boolean;
  work_order?: {
    id: string;
    order_number: string;
    status: string;
  } | null;
}

export default function Molds() {
  const { userRole } = useAuth();
  const { language } = useLanguage();
  const locale = language === "es" ? es : enUS;
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMold, setSelectedMold] = useState<Mold | null>(null);
  const [viewMoldId, setViewMoldId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get('focus');

  const [locationFilter, setLocationFilter] = useState("all");

  const { data: molds, isLoading } = useQuery({
    queryKey: ["molds"],
    queryFn: async () => {
      const { data, error } = await api
        .from("molds")
        .select("*, work_order:work_orders!molds_current_work_order_id_fkey(id, order_number, status)")
        .order("name");

      if (error) throw error;
      return data as Mold[];
    },
  });

  // Auto-abrir el dialog cuando llega ?focus=ID en la URL
  useEffect(() => {
    if (focusId && molds && molds.length > 0) {
      const target = molds.find((m) => m.id === focusId);
      if (target) {
        setSelectedMold(target);
        setDialogOpen(true);
        // limpiar el query param para evitar re-abrir
        searchParams.delete('focus');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [focusId, molds]);

  // Filter molds
  const filteredMolds = molds?.filter((mold) => {
    const matchesSearch =
      searchTerm === "" ||
      mold.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mold.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mold.current_location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mold.location?.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesStatus = true;
    if (statusFilter === "active") {
      matchesStatus = mold.is_active;
    } else if (statusFilter === "inactive") {
      matchesStatus = !mold.is_active;
    } else if (statusFilter === "maintenance") {
      matchesStatus = mold.max_uses ? mold.current_uses >= mold.max_uses * 0.8 : false;
    } else if (statusFilter === "critical") {
      matchesStatus = mold.max_uses ? mold.current_uses >= mold.max_uses : false;
    } else if (statusFilter === "overdue") {
      matchesStatus = mold.next_maintenance_date
        ? isBefore(new Date(mold.next_maintenance_date), new Date())
        : false;
    } else if (statusFilter === "in_use") {
      matchesStatus = !!mold.current_work_order_id;
    } else if (statusFilter === "available") {
      matchesStatus = mold.is_active && !mold.current_work_order_id;
    }

    const matchesLocation = locationFilter === "all" ||
      mold.current_location === locationFilter;

    return matchesSearch && matchesStatus && matchesLocation;
  });

  // Calculate stats
  const stats = {
    total: molds?.length || 0,
    active: molds?.filter((m) => m.is_active).length || 0,
    inUse: molds?.filter((m) => m.current_work_order_id).length || 0,
    available: molds?.filter((m) => m.is_active && !m.current_work_order_id).length || 0,
    needsMaintenance: molds?.filter((m) => m.max_uses && m.current_uses >= m.max_uses * 0.8).length || 0,
    critical: molds?.filter((m) => m.max_uses && m.current_uses >= m.max_uses).length || 0,
    overdue: molds?.filter((m) => m.next_maintenance_date && isBefore(new Date(m.next_maintenance_date), new Date())).length || 0,
  };

  // Get unique locations for filter
  const locations = [...new Set(molds?.map(m => m.current_location).filter(Boolean))] as string[];

  const getMoldStatus = (mold: Mold) => {
    if (!mold.is_active) return { label: "Inactivo", color: "bg-gray-500/10 text-gray-500" };

    const maintenanceOverdue = mold.next_maintenance_date &&
      isBefore(new Date(mold.next_maintenance_date), new Date());
    if (maintenanceOverdue) return { label: "Mant. Vencido", color: "bg-red-500/10 text-red-500" };

    if (mold.max_uses && mold.current_uses >= mold.max_uses) {
      return { label: "Requiere Mant.", color: "bg-red-500/10 text-red-500" };
    }
    if (mold.max_uses && mold.current_uses >= mold.max_uses * 0.8) {
      return { label: "Advertencia", color: "bg-orange-500/10 text-orange-500" };
    }

    const maintenanceSoon = mold.next_maintenance_date &&
      isBefore(new Date(mold.next_maintenance_date), addDays(new Date(), 7));
    if (maintenanceSoon) return { label: "Mant. Proximo", color: "bg-yellow-500/10 text-yellow-500" };

    return { label: "Activo", color: "bg-green-500/10 text-green-500" };
  };

  const handleEdit = (mold: Mold) => {
    setSelectedMold(mold);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedMold(null);
    setDialogOpen(true);
  };

  const canManage = userRole === "admin" || userRole === "supervisor";

  // Show mold details
  if (viewMoldId) {
    return (
      <DashboardLayout>
        <MoldDetails moldId={viewMoldId} onBack={() => setViewMoldId(null)} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Gestion de Moldes
            </h1>
            <p className="text-muted-foreground mt-1">
              Control de moldes de produccion y mantenimiento
            </p>
          </div>
          {canManage && (
            <Button onClick={handleNew}>
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Molde
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Box className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.available}</p>
                  <p className="text-sm text-muted-foreground">Disponibles</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-500/10">
                  <FileText className="w-5 h-5 text-cyan-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.inUse}</p>
                  <p className="text-sm text-muted-foreground">En Uso (OT)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/10">
                  <Wrench className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.needsMaintenance}</p>
                  <p className="text-sm text-muted-foreground">Advertencia</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.critical}</p>
                  <p className="text-sm text-muted-foreground">Criticos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Calendar className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.overdue}</p>
                  <p className="text-sm text-muted-foreground">Mant. Vencido</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, codigo o ubicacion..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="available">Disponibles</SelectItem>
              <SelectItem value="in_use">En Uso (OT)</SelectItem>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="inactive">Inactivos</SelectItem>
              <SelectItem value="maintenance">Advertencia</SelectItem>
              <SelectItem value="critical">Criticos</SelectItem>
              <SelectItem value="overdue">Mant. Vencido</SelectItem>
            </SelectContent>
          </Select>
          <Select value={locationFilter} onValueChange={setLocationFilter}>
            <SelectTrigger className="w-[180px]">
              <MapPin className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Ubicacion" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las ubicaciones</SelectItem>
              {locations.map((loc) => (
                <SelectItem key={loc} value={loc}>{loc}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Molds Grid */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          </div>
        ) : filteredMolds && filteredMolds.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredMolds.map((mold) => {
              const status = getMoldStatus(mold);
              const usagePercent = mold.max_uses
                ? Math.round((mold.current_uses / mold.max_uses) * 100)
                : 0;
              const needsMaintenance = mold.max_uses && mold.current_uses >= mold.max_uses;
              const nearMaintenance = mold.max_uses && mold.current_uses >= mold.max_uses * 0.8;

              return (
                <Card
                  key={mold.id}
                  className={cn(
                    "cursor-pointer hover:shadow-lg transition-all",
                    needsMaintenance && "border-red-500/50",
                    nearMaintenance && !needsMaintenance && "border-orange-500/50"
                  )}
                  onClick={() => setViewMoldId(mold.id)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{mold.name}</h3>
                        {mold.code && (
                          <p className="text-sm text-muted-foreground">
                            Codigo: {mold.code}
                          </p>
                        )}
                      </div>
                      <Badge className={status.color}>{status.label}</Badge>
                    </div>

                    {mold.description && (
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {mold.description}
                      </p>
                    )}

                    <div className="space-y-3">
                      {/* Work Order Status */}
                      {mold.work_order ? (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            OT Activa
                          </span>
                          <Badge variant="secondary" className="bg-cyan-500/10 text-cyan-600">
                            {mold.work_order.order_number}
                          </Badge>
                        </div>
                      ) : mold.is_active ? (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Estado</span>
                          <Badge variant="secondary" className="bg-green-500/10 text-green-600">
                            Disponible
                          </Badge>
                        </div>
                      ) : null}

                      {/* Current Location */}
                      {mold.current_location && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            Ubicacion
                          </span>
                          <span className="font-medium">
                            {mold.current_location}
                            {mold.current_location === 'Almacén' && mold.shelf_location && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                Est. {mold.shelf_location}
                              </Badge>
                            )}
                          </span>
                        </div>
                      )}

                      {/* NFC Status */}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Nfc className="w-3 h-3" />
                          NFC
                        </span>
                        {mold.nfc_tag_uid ? (
                          <Badge variant="secondary" className="font-mono text-xs">
                            {mold.nfc_tag_uid.slice(0, 8)}...
                          </Badge>
                        ) : (
                          <span className="text-orange-500 text-xs">Sin asignar</span>
                        )}
                      </div>

                      {/* Usage Progress */}
                      {mold.max_uses && (
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Ciclos de Uso</span>
                            <span className="font-medium">
                              {mold.current_uses} / {mold.max_uses}
                            </span>
                          </div>
                          <Progress
                            value={usagePercent}
                            className={cn(
                              "h-2",
                              needsMaintenance && "[&>div]:bg-red-500",
                              nearMaintenance && !needsMaintenance && "[&>div]:bg-orange-500"
                            )}
                          />
                        </div>
                      )}

                      {/* Next Maintenance */}
                      {mold.next_maintenance_date && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Prox. Mant.</span>
                          <span className={cn(
                            isBefore(new Date(mold.next_maintenance_date), new Date()) && "text-red-500 font-medium"
                          )}>
                            {format(new Date(mold.next_maintenance_date), "PP", { locale })}
                          </span>
                        </div>
                      )}
                    </div>

                    {canManage && (
                      <div className="mt-4 pt-4 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(mold);
                          }}
                        >
                          Editar
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Wrench className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-2">
                {searchTerm || statusFilter !== "all"
                  ? "No se encontraron moldes con los filtros seleccionados"
                  : "No hay moldes registrados"}
              </p>
              {canManage && (
                <Button onClick={handleNew} className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Primer Molde
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <MoldFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mold={selectedMold}
      />
    </DashboardLayout>
  );
}
